import React from 'react'
import { useEntry } from 'intersection-observer'
import { useDevice } from 'device'
import { usePathname } from 'router'
import links from 'links'
import { useAb } from 'hooks'
import { constants } from 'helpers'

import ProductsInQueueProvider from 'containers/ProductsInQueueProvider/ProductsInQueueProvider'

import ContinueStickyBar from 'compositions/landings/ContinueStickyBar/ContinueStickyBar'

import HeroSection, { type HeroSectionProps } from './components/HeroSection/HeroSection'
import BrandsRow from './components/BrandsRow/BrandsRow'
import HowItWorksSection from './components/HowItWorksSection/HowItWorksSection'
import BrandsSection from './components/BrandsSection/BrandsSection'
import PressSection from './components/PressSection/PressSection'
import QuizSection from './components/QuizSection/QuizSection'
import BestsellersSection from './components/BestsellersSection/BestsellersSection'
import BottomBanner from './components/BottomBanner/BottomBanner'

import messages from './messages'


type HomePageViewProps = {
  isFetching: boolean
  price: string
  eventDiscountText?: SubscriptionModule.EventDiscountText
  offerType?: SubscriptionModule.OfferType
  discount?: string
  priceWithDiscount?: string
  extraShippingPrice?: string
  couponCode?: string
  heroCustomTexts?: HeroSectionProps['customTexts']
  heroCustomTextStyles?: HeroSectionProps['customTextStyles']
  withHeroLink?: boolean
}

const View: React.FunctionComponent<HomePageViewProps> = (props) => {
  const {
    couponCode,
    discount,
    eventDiscountText,
    extraShippingPrice,
    heroCustomTexts,
    heroCustomTextStyles,
    isFetching,
    offerType,
    price,
    priceWithDiscount,
    withHeroLink,
  } = props

  const { isMobile } = useDevice()

  const defaultSectionMargin = isMobile ? 'mt-64' : 'mt-80'

  const pathname = usePathname()

  const isHomePage = (
    pathname === links.home
    || pathname === links.homeMen
  )

  const isStartButtonWithPriceEnabled = (
    useAb(constants.abTests.homeChangeBannerText) === 'b'
    && offerType === 'discount'
    && isHomePage
  )

  const startButtonWithPriceMessage = {
    ...messages.getStartedForPrice, values: { price: priceWithDiscount },
  }

  return (
    <ProductsInQueueProvider>
      <HeroSection
        isFetching={isFetching}
        offerType={offerType}
        discount={discount}
        price={price}
        priceWithDiscount={priceWithDiscount}
        extraShippingPrice={extraShippingPrice}
        customTexts={heroCustomTexts}
        customTextStyles={heroCustomTextStyles}
        isTimerEnabled={isHomePage}
        withHeroLink={withHeroLink}
        startButtonMessage={isStartButtonWithPriceEnabled ? startButtonWithPriceMessage : undefined}
        // TODO: if CRO-144 ab test wins
        // Improve and use the code of passing customTexts to Hero instead of separate custom note message
        noteMessage={isStartButtonWithPriceEnabled && !isMobile ? null : undefined}
      />
      <BrandsRow />
      <HowItWorksSection
        className={defaultSectionMargin}
        startButtonMessage={isStartButtonWithPriceEnabled && isMobile ? startButtonWithPriceMessage : undefined}
      />
      <BrandsSection />
      <QuizSection className={isMobile ? null : defaultSectionMargin} />
      <PressSection className={defaultSectionMargin} />
      <StickyBarContainer
        defaultSectionMargin={defaultSectionMargin}
        eventDiscountText={eventDiscountText}
        couponCode={couponCode}
      />
      <BottomBanner className={defaultSectionMargin} />
    </ProductsInQueueProvider>
  )
}

const StickyBarContainer: React.FC<{ defaultSectionMargin: string, eventDiscountText: SubscriptionModule.EventDiscountText, couponCode: string }> = (props) => {
  const { defaultSectionMargin, eventDiscountText, couponCode } = props

  // narrow rootMargin to tiny line to hide StickyBar before global footer
  const [ stickyBarAreaRef, stickyBarAreaEntry ] = useEntry({ observerProps: { rootMargin: '0% 0% -90%' } })
  const isStickyBarVisible = stickyBarAreaEntry?.isIntersecting

  return (
    <div ref={stickyBarAreaRef}>
      <BestsellersSection className={defaultSectionMargin} />
      <ContinueStickyBar
        isVisible={isStickyBarVisible}
        eventDiscountText={eventDiscountText}
        coupon={couponCode}
      />
    </div>
  )
}


export default View
